import React from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useShipmentContext } from '../../../components/ShipmentProvider';
import Avatar from '@material-ui/core/Avatar';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Skeleton from '@material-ui/lab/Skeleton';
import DataGrid from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import fetchMultipleProducts from '@afosto/utils-react/fetchMultipleProducts';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import uuid from '@afosto/utils/uuid';
import { productImageFormatter, productSkuFormatter } from './formatters';

const DataListContainerGrid = props => <Grid container spacing={1} {...props} />;

const ShipmentOverage = () => {
  const intl = useIntl();
  const { shipment } = useShipmentContext();

  const fetchItems = async () => {
    try {
      const overage = shipment?.overage ?? [];
      const products = await fetchMultipleProducts(overage.map(item => item.sku));

      return overage.reduce((acc, item) => {
        const product = findObjectInArray(products, 'sku', item.sku);

        return [
          ...acc,
          {
            ...item,
            id: uuid(),
            product,
          },
        ];
      }, []);
    } catch (err) {
      return [];
    }
  };

  const dataSource = useDataGrid(fetchItems, {
    modules: ['dataProperties'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'image',
          name: '',
          type: 'productImage',
          width: '50px',
        },
        {
          key: 'sku',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingOverage.sku',
            defaultMessage: 'SKU',
            description: 'The column name for column sku',
          }),
          type: 'productSku',
        },
        {
          key: 'description',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingOverage.description',
            defaultMessage: 'Description',
            description: 'The column name for column description',
          }),
        },
        {
          key: 'position',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingOverage.position',
            defaultMessage: 'Position',
            description: 'The column name for column position',
          }),
        },
        {
          key: 'quantity',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingOverage.quantity',
            defaultMessage: 'Quantity',
            description: 'The column name for column quantity',
          }),
        },
      ],
    },
  });

  const renderItem = item => (
    <Grid key={item.id} item xs={12}>
      <Card>
        <CardContent>
          <List disablePadding>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Avatar src={item.product && item.product.image} />
              </ListItemAvatar>
              <ListItemText primary={item.description} secondary={item.sku} />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderItemSkeleton = key => (
    <Grid key={key} item xs={12}>
      <Card>
        <CardContent>
          <List disablePadding>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Skeleton variant="circle" width={40} height={40} />
              </ListItemAvatar>
              <ListItemText
                primary={<Skeleton variant="text" />}
                secondary={<Skeleton variant="text" />}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <>
      <Hidden implementation="js" mdUp>
        <DataList
          component={DataListContainerGrid}
          dataSource={dataSource}
          renderItem={renderItem}
          renderSkeletonItem={renderItemSkeleton}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid
          dataSource={dataSource}
          formatters={{ productImage: productImageFormatter, productSku: productSkuFormatter }}
        />
      </Hidden>
    </>
  );
};

export default ShipmentOverage;
